import ObjectId from "bson-objectid";
import { z, ZodError } from "zod";

import { assertEqualTypes } from "./assertEqualTypes";
import { COUNTRY_CODES } from "../constants/COUNTRIES";
import { ISO8601_REGEX } from "../constants/ISO8601_REGEX";

import type { CountryCode } from "../constants/COUNTRIES";

function isObjectIdString(value: unknown): value is string {
  return (
    typeof value === "string" && value.length === 24 && ObjectId.isValid(value)
  );
}

export const objectIdSchema = z
  .string()
  .refine(
    (v) => isObjectIdString(v),
    (v) =>
      new ZodError([
        {
          code: "custom",
          message: `Invalid ObjectId: ${v}`,
          path: ["some_path"],
        },
      ]),
  )
  .transform((v) => new ObjectId(v));

export const stringToNumberSchema = z
  .union([z.string(), z.number()])
  .refine(
    (v) => {
      if (typeof v === "number") return true;
      const parsed = Number(v);
      return !isNaN(parsed);
    },
    {
      message: "Invalid number or string in stringToNumberSchema",
    },
  )
  .transform((v): number => (typeof v === "number" ? v : Number(v)));

export const dateSchema = z
  .string()
  .refine(
    (dateString) => {
      return ISO8601_REGEX.test(dateString);
    },
    {
      message: "Invalid date string",
    },
  )
  .transform((dateString) => new Date(dateString));

function convertAllObjectId(input: unknown): unknown {
  if (Array.isArray(input)) {
    return input.map((item) => convertAllObjectId(item));
  } else if (isObjectIdString(input)) {
    return new ObjectId(input);
  } else if (
    typeof input === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(input)
  ) {
    return new Date(input);
  } else if (input !== null && typeof input === "object") {
    return Object.entries(input).reduce<Record<string, unknown>>(
      (acc, [key, value]) => {
        acc[key] = convertAllObjectId(value);
        return acc;
      },
      {},
    );
  }
  return input;
}

export const anonymousObjectSchema = <T extends object = object>() =>
  z
    .object({})
    .passthrough()
    .transform((v) => convertAllObjectId(v) as T);

export const countryCodeWithLowerCaseSchema = z
  .string()
  .transform((v) => v.toUpperCase())
  .pipe(
    z.union([
      z.literal("419"), // https://en.wikipedia.org/wiki/Language_code
      z.enum(COUNTRY_CODES as [CountryCode, ...CountryCode[]]),
    ]),
  )
  .transform((v) => (v === "419" ? undefined : v));

export const customPropertiesRequestSchema = z.record(
  z.union([
    dateSchema,
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.array(z.object({})),
    z.object({}),
    z.null(),
    z.undefined(),
  ]),
);

type CustomPropertiesRequest = z.infer<typeof customPropertiesRequestSchema>;

export const customPropertiesSchema = z.record(
  z.union([
    dateSchema,
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.array(z.object({})),
    z.object({}),
    z.undefined(),
  ]),
);

export type CustomProperties = z.infer<typeof customPropertiesSchema>;

assertEqualTypes<
  {
    [K in keyof CustomPropertiesRequest]: Exclude<
      CustomPropertiesRequest[K],
      null
    >;
  },
  CustomProperties
>(true);

export const stringToArraySchema = z
  .string()
  .transform((v) => v.split(",").map((item) => item.trim()))
  .refine((arr) => arr.every((item) => item.length > 0), {
    message: "Each item should not be empty",
  });

export const eventPropertiesSchema = z.object({
  section: z.string().optional(),
  prev_section: z.string().optional(),
  recommendation_id: z.string().optional(),
  price: z.number().optional(),
  order_id: z.string().optional(),
  rating: z.number().optional(),
  prev_page: z.string().optional(),
  page: z.string().optional(),
  position: z.number().optional(),
  item_id: z.string().optional(),
  items: z
    .array(
      z.object({
        id: z.string(),
        price: z.number(),
        quantity: z.number().optional(),
        custom_event_properties: customPropertiesSchema.optional(),
      }),
    )
    .optional(),
  search_query: z.string().optional(),
  order_amount: z.number().optional(),
  paid_amount: z.number().optional(),
  tracking: z
    .object({
      id: z.string(),
      type: z.string(),
    })
    .optional(),
  click_target: z.string().optional(),
});

export type EventProperties = z.infer<typeof eventPropertiesSchema>;

export const internalEventPropertiesSchema = z.object({
  url: z.string().optional(),
  ref: z.string().optional(),
  click_target: z.string().optional(),
});

export type InternalEventProperties = z.infer<
  typeof internalEventPropertiesSchema
>;

// Schema for Inference API
const inferenceRequestSchema = z.object({
  application_id: objectIdSchema,
  blux_user_id: objectIdSchema.nullish(),

  pipeline: z.array(
    z.object({
      name: z.string(),
      kwargs: z.record(z.any()),
    }),
  ),
  item_id: z.string().nullish(),
  item_ids: z.array(z.string()).nullish(),

  limit: z.number(),
  offset: z.number().nullish().default(0),
  options: z.record(z.string(), z.any()).nullish(), // TODO: @all legacy 클라이언트 migration만을 위한 필드라는 점을 고려하여 필드명 수정 필요 ex. legacy_options
});

export const recommendationRequestSchema = inferenceRequestSchema
  .pick({
    item_id: true,
    item_ids: true,
    limit: true,
    offset: true,
  })
  .extend({
    call_type: z.string(),
    category_id: z.string().nullish(),
  });

export type RecResponse = {
  item_ids: string[];
  metadata: Record<string, unknown>;
  recommendation_id: string | null;
};
